import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import { useAuthActions } from '@frontegg/react';
import Dialog from '../base-dialog/base-dialog.component';
import STRING_KEYS from '../../../language/keys';
import { DialogWidthSize } from '../base-dialog/base-dialog.enums';
import { useGetMyUserQuery } from '../../../store/rtk-query/apis/user/user.toolkit-api';
import { THEME } from '../../../themes';
import { useAnalyticsContext } from '../../../contexts/analytics/useAnalyticsContext';
import { ANALYTICS_EVENTS, EVENT_GROUP } from '../../../consts/analytics';
import { BillingPeriod } from '../../../enums/billing-period.enum';
import { useGetAvailablePlansQuery } from '../../../store/rtk-query/apis/account-plan/account-plan.toolkit-api';
import { useGetAccountByIdQuery } from '../../../store/rtk-query/apis/account/account.toolkit-api';
import PerfectButton from '../../Common/Buttons/perfect-base-button/perfect-base-button.component';
import { PerfectButtonVariants } from '../../Common/Buttons/perfect-base-button/perfect-base-button.enums';
import { UpgradePlanDialogVariation } from './upgrade-plan-dialog.enums';
import { UpgradePlanDialogProps } from './upgrade-plan-dialog';
import {
  AvailablePlansWrapper,
  BillingPeriodOption,
  BillingPeriodSelect,
  Container,
  Description,
  Perks,
  StyledBody,
  Subtitle,
  TextContainer,
  Title,
  YearlyDiscount,
  YearlyWrapper,
} from './upgrade-plan-dialog.styled';
import { PricingPlanCard } from './pricing-plan-card/PricingPlanCard';
import { CurrentPlanDetails } from './current-plan-details/CurrentPlanDetails';
 
export const UpgradePlanDialog = ({
  onCloseDialog,
  isDialogOpen,
  variation,
}: UpgradePlanDialogProps): JSX.Element => {
  const { t: translate } = useTranslation();

  const { analyticsTrackEvent } = useAnalyticsContext();

  const { logout } = useAuthActions();

  const { data: { accountId } = {} } = useGetMyUserQuery();
  const {
    data: { recruitingUsageType } = {},
  } = useGetAccountByIdQuery(accountId, { skip: !accountId });
  const { data: { plans = [] } = {} } = useGetAvailablePlansQuery({
    recruitingUsageType,
  });

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(
    BillingPeriod.Yearly,
  );

  useEffect(() => {
    if (isDialogOpen && variation === UpgradePlanDialogVariation.TrialEnded) {
      analyticsTrackEvent({
        eventName: ANALYTICS_EVENTS.TRIAL_ENDED_MODAL_SHOWN,
        eventGroup: EVENT_GROUP.CLICK,
      });
    }
  }, [analyticsTrackEvent, isDialogOpen, variation]);

  let title: string = translate(STRING_KEYS.UPGRADE_PLAN_DIALOG.DEFAULT.TITLE);
  let subtitle: string = translate(
    STRING_KEYS.UPGRADE_PLAN_DIALOG.DEFAULT.SUBTITLE,
  );
  let description: string = null;

  switch (variation) {
    case UpgradePlanDialogVariation.TrialEnded:
      title = translate(STRING_KEYS.UPGRADE_PLAN_DIALOG.TRIAL_ENDED.TITLE);
      subtitle = translate(
        STRING_KEYS.UPGRADE_PLAN_DIALOG.TRIAL_ENDED.SUBTITLE,
      );
      description = translate(
        STRING_KEYS.UPGRADE_PLAN_DIALOG.TRIAL_ENDED.DESCRIPTION,
      );
      break;
    case UpgradePlanDialogVariation.MatchDailyLimit:
      title = translate(
        STRING_KEYS.UPGRADE_PLAN_DIALOG.MATCH_DAILY_LIMIT.TITLE,
      );
      subtitle = translate(
        STRING_KEYS.UPGRADE_PLAN_DIALOG.MATCH_DAILY_LIMIT.SUBTITLE,
      );
      break;
    case UpgradePlanDialogVariation.ExceedPositionLimit:
      title = translate(STRING_KEYS.UPGRADE_PLAN_DIALOG.DEFAULT.SUBTITLE);
      subtitle = translate(
        STRING_KEYS.UPGRADE_PLAN_DIALOG.EXCEED_POSITION_LIMIT.TITLE,
      );
      break;
    case UpgradePlanDialogVariation.NeedMoreMatches:
      title = translate(STRING_KEYS.UPGRADE_PLAN_DIALOG.DEFAULT.SUBTITLE);
      subtitle = translate(
        STRING_KEYS.UPGRADE_PLAN_DIALOG.NEED_MORE_MATCHES.TITLE,
      );
      break;
    default:
      break;
  }

  const handleBillingPeriodChange = (period: string) => {
    setBillingPeriod(period as BillingPeriod);

    return true;
  };

  return (
    <Dialog
      isOpen={ isDialogOpen }
      onClose={ onCloseDialog }
      widthSize={ DialogWidthSize.XXLarge }
      disableCloseButton={ variation === UpgradePlanDialogVariation.TrialEnded }
      portalUniqueId="upgrade-plan"
      zIndex={ THEME.Z_INDEX.HIGH + 2001 }
      isFullScreen
      backgroundColor="secondary"
    >
      <StyledBody>
        <Container>
          <CurrentPlanDetails />

          <TextContainer>
            <Title>{title}</Title>
            
            <Subtitle>{subtitle}</Subtitle>

            {description && <Description>{description}</Description>}
          </TextContainer>

          <BillingPeriodSelect
            clickHandler={ handleBillingPeriodChange }
            defaultSelectedIndex={ 1 }
          >
            <BillingPeriodOption value={ BillingPeriod.Monthly }>
              {translate(
                STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.PRICE_INTERVAL.PAY_MONTHLY,
              )}
            </BillingPeriodOption>

            <BillingPeriodOption value={ BillingPeriod.Yearly }>
              <YearlyWrapper>
                {translate(
                  STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.PRICE_INTERVAL
                    .PAY_YEARLY,
                )}
                <YearlyDiscount>
                  {translate(
                    STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.PRICE_INTERVAL
                      .YEARLY_DISCOUNT,
                  )}
                </YearlyDiscount>
              </YearlyWrapper>
            </BillingPeriodOption>
          </BillingPeriodSelect>

          <AvailablePlansWrapper>
            {cloneDeep(plans)
              .sort((a, b) => b.pricingPlan.localeCompare(a.pricingPlan))
              .map((availablePlan) => (
                <PricingPlanCard
                  key={ availablePlan.id }
                  plan={ availablePlan }
                  billingPeriod={ billingPeriod }
                />
              ))}
          </AvailablePlansWrapper>

          <Perks>
            {translate(STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.PERKS)}
          </Perks>

          <PerfectButton variant={ PerfectButtonVariants.Link } onClick={ () => logout() }>
            {translate(STRING_KEYS.UPGRADE_PLAN_DIALOG.PLANS.LOGOUT)}
          </PerfectButton>
        </Container>
      </StyledBody>
    </Dialog>
  );
};
